import { useCallback } from "react";
import { COUNTRY_ID, COUNTRY_MAP } from "../constants/countries";
import { useTranslation } from "next-i18next";
import { DUMMY_PRICE } from "@/common/static/checkout/checkout.static";
import { CountryUtils } from "@/utils/CountryUtils";

type Product = {
  address?: {
    city: string;
    country: string;
  } | null;
  shipping_method?: {
    slug: string;
  } | null;
  custom_shipping_cost?: {
    amount: number;
    currency: string;
  } | null;
  shipping_method_prices?: {
    country: string;
    price: {
      amount: number;
      currency: string;
    };
  }[];
};

const useProductUtils = () => {
  const { t, i18n } = useTranslation();

  const getLocation = useCallback(
    (product: Product) => {
      const city = product.address?.city;
      const countryCode = product.address?.country;

      if (!countryCode) {
        return city;
      }

      const countryName = CountryUtils.getCountryName(
        countryCode,
        i18n.language
      );

      return `${city}, ${countryName}`;
    },
    [i18n.language]
  );

  const getLocationFlagComponent = useCallback((product: Product) => {
    const countryCode = product.address?.country as COUNTRY_ID | undefined; // Hate this `as`. It's wrong.

    if (!countryCode) {
      return null;
    }

    const country = COUNTRY_MAP[countryCode];

    // The below condition is possible, but TS doesn't know that becuase of the `as` in the first
    // line of the function.
    if (!country) {
      return null;
    }

    return country.flag;
  }, []);

  const getShippingMethodPrice = useCallback(
    (product: Product) => {
      const countryCode = product.address?.country as COUNTRY_ID | undefined;

      const methodPice = product.shipping_method_prices?.find(
        price => price.country === countryCode
      );

      if (!methodPice && product.shipping_method?.slug === "custom") {
        // If we couldn't find a price for the country and the shipping method
        // is custom, we should show the custom price. This is because we
        // allow custom shipping method from non-supported countries like
        // Ireland, etc.
        return t("common:common-price", {
          price: product.custom_shipping_cost,
        });
      }
      return t("common:common-price", {
        price: methodPice?.price ?? DUMMY_PRICE,
      });
    },
    [t]
  );

  return { getLocation, getShippingMethodPrice, getLocationFlagComponent };
};

export default useProductUtils;
